import { useEffect } from 'react';
import { CalendarDays, Info, Repeat, X } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { useSheetDrag } from '../../lib/useSheetDrag';
import { formatPrice, formatPriceOrFree, type BlockPreview, type BlockWeek } from './bookingApi';
import './booking.css';

const SKIP_LABELS: Record<string, string> = {
  Past: 'In the past',
  Closed: 'Closed',
  OutsideHours: 'Outside opening hours',
  HardClash: 'Already booked',
  BufferClash: 'Changeover clash',
};

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/London',
  });

/**
 * Step two of a block booking: every weekly occurrence with its price or the reason it can't be
 * booked, plus the total — nothing is booked or charged until the coach confirms here.
 */
export function BlockPreviewSheet({
  pitchName,
  preview,
  foc = false,
  paymentRequest = false,
  busy,
  onConfirm,
  onBack,
}: {
  pitchName: string;
  preview: BlockPreview;
  /** Admin FOC block: weeks still carry nominal prices server-side, but nothing is billed. */
  foc?: boolean;
  /** Admin on-behalf block: the coach owes each week — payment requests, no checkout leg. */
  paymentRequest?: boolean;
  busy: boolean;
  onConfirm: () => void;
  onBack: () => void;
}) {
  const sheetRef = useSheetDrag(onBack);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onBack();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onBack]);

  const bookable = preview.weeks.filter((w) => w.status === 'Ok');
  const skipped = preview.weeks.filter((w) => w.status !== 'Ok');
  const canConfirm = bookable.length > 0 && !preview.priceMissing;
  // Weeks are billed one at a time: the whole block is held, but only the first PAYABLE week is
  // due now — £0 weeks (free-of-charge pricing) confirm at booking time and are never billed.
  // An FOC grant bills nothing at all, whatever the price rules say.
  const dueNow = foc ? 0 : bookable.find((w) => (w.pricePence ?? 0) > 0)?.pricePence ?? 0;

  return (
    <div className="sheet-backdrop" role="presentation" onClick={onBack}>
      <div
        ref={sheetRef}
        className="sheet bk-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={`Block book ${pitchName}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bk-sheet-handle" aria-hidden />
        <button type="button" className="bk-sheet-close" aria-label="Close" onClick={onBack}>
          <X size={18} aria-hidden />
        </button>

        <header className="bk-sheet-header">
          <div className="bk-sheet-title-row">
            <strong className="bk-sheet-title">{pitchName}</strong>
            <span className="pitch-format-badge">
              <Repeat size={12} aria-hidden /> Weekly
            </span>
          </div>
          <div className="bk-sheet-when">
            <span>
              <CalendarDays size={14} aria-hidden />
              Every {preview.dayOfWeek} · {preview.weeks.length} week{preview.weeks.length === 1 ? '' : 's'}
            </span>
          </div>
        </header>

        <div className="bk-section">
          <span className="bk-section-label">Your weeks</span>
          <ul className="bk-week-list">
            {preview.weeks.map((w) => (
              <WeekRow key={w.date} week={w} foc={foc} />
            ))}
          </ul>
        </div>

        <div className="bk-block-total">
          <span>
            {bookable.length} week{bookable.length === 1 ? '' : 's'} bookable
            {skipped.length > 0 ? ` · ${skipped.length} unavailable` : ''}
          </span>
          <strong>
            {foc || preview.totalPence === 0 ? 'Free of charge' : `${formatPrice(preview.totalPence)} total`}
          </strong>
        </div>

        <div className="bk-hints">
          <Info size={15} aria-hidden />
          <div>
            {foc ? (
              <>
                <p>Unavailable weeks are simply not booked.</p>
                <p>
                  Free of charge — every week confirms immediately with nothing to pay, and the
                  slot is reserved for the team from the moment you book.
                </p>
              </>
            ) : paymentRequest ? (
              <>
                <p>Unavailable weeks are not booked and not billed.</p>
                <p>
                  Every week is booked for the team straight away and billed to the coach — a
                  payment request is emailed, each week is paid separately from My payments, and
                  the club chases what's owed. Nothing for you to pay now.
                </p>
              </>
            ) : (
              <>
                <p>
                  Unavailable weeks are not booked and not charged — you only pay for the weeks
                  listed with a price.
                </p>
                <p>
                  You pay for one week at a time. Only the first week is due now; we&apos;ll remind
                  you before each later week. All the weeks are reserved for your team from the
                  moment you book.
                </p>
                <p>The first week is held for a short time while you pay. Price is confirmed at checkout.</p>
              </>
            )}
          </div>
        </div>

        {preview.priceMissing ? (
          <p className="bk-block-error" role="alert">
            A price is missing for this pitch — contact the club before block booking.
          </p>
        ) : null}

        <div className="sheet-actions">
          <Button variant="secondary" onClick={onBack} disabled={busy}>
            Back
          </Button>
          <Button loading={busy} disabled={!canConfirm} onClick={onConfirm}>
            {!canConfirm
              ? 'Nothing to book'
              : paymentRequest
                ? `Book ${bookable.length} week${bookable.length === 1 ? '' : 's'} & send payment request`
                : dueNow === 0
                  ? `Book ${bookable.length} week${bookable.length === 1 ? '' : 's'} free of charge`
                  : `Book ${bookable.length} week${bookable.length === 1 ? '' : 's'} & pay ${formatPrice(dueNow)} now`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function WeekRow({ week, foc }: { week: BlockWeek; foc: boolean }) {
  const ok = week.status === 'Ok';
  return (
    <li className={`bk-week ${ok ? '' : 'bk-week-skipped'}`}>
      <span className="bk-week-date">{fmtDate(week.startUtc)}</span>
      <span className="bk-week-time">
        {fmtTime(week.startUtc)}–{fmtTime(week.endUtc)}
      </span>
      {ok ? (
        <span className="bk-week-price">
          {foc ? 'Free' : week.pricePence !== null ? formatPriceOrFree(week.pricePence) : '—'}
        </span>
      ) : (
        <span className="bk-week-skip" title={week.detail ?? undefined}>
          {SKIP_LABELS[week.status] ?? week.status}
        </span>
      )}
    </li>
  );
}
